import React, {
  useState,
  useEffect,
  useRef,
  memo,
  useLayoutEffect,
} from 'react';
import Scrollbar from 'react-smooth-scrollbar';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { connect } from 'react-redux';
import Stomp from 'stompjs';
import SockJS from 'sockjs-client';
import { getToken } from '../../../auth';
import ChatContact from './ChatContact';
import ChatSearch from './ChatSearch';
import ChatBubble from './ChatBubble';
import ChatField from './ChatField';
import ChatTopbar from './ChatTopbar';
import UserCard from './UserCard';
import {
  CurrentUserProps,
  ContactsProps,
} from '../../../shared/prop-types/ChatProps';
import {
  CHAT_CONTACT_TYPES,
  CHAT_RECORD_TYPE,
  CONTACT_CHAT_TYPE,
  DEFAULT_SMART_TALK_API_URL,
  FILE_TO_UPLOAD,
  REPORT_PARAMETERS,
  REPORT_TYPES,
} from '../../../shared/utils/constants';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../../redux/actions/loadingActions';
import updatePendencySummary from '../../../redux/actions/pendencyActions';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import {
  loadPendencyQuantity,
  upLoadFileProvider,
  generateReport,
  openReport,
  getFormattedDateEN,
  newUTCDate,
} from '../../../shared/utils/smartutils';
import smarttalkaxios from '../../../smarttalkaxios';
import {
  getCurrentMessagesStorage,
  getSelectedContactStorage,
  setCurrentMessagesStorage,
  setSelectedContactStorage,
} from '../../../sessionStorage';
import LoadMoreButton from './LoadMoreButton';

let stompClient;
const Chat = ({
  currentUser,
  contacts,
  dispatch,
  loadChatContacts,
  t,
  pendencySummary,
  contactType,
  channelId,
  userChatId,
}) => {
  const [currentMessages, setCurrentMessages] = useState([]);
  const [openContacts, setOpenContacts] = useState(false);
  const [filteredContactList, setFilteredContactList] = useState([]);
  const [filterChecked, setFilterChecked] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [selectedContact, setSelectedContact] = useState({});
  const [selectedChatChannel, setSelectedChatChannel] = useState({});
  const [uploadFileError, setUploadFileError] = useState();
  const [loadingMore, setLoadingMore] = useState(false);
  const currentChannelIdRef = useRef(channelId);
  const lastOffSetHeight = useRef(0);

  const token = getToken();
  const chatScrollBar = useRef();

  function showLoading() {
    dispatch(changeLoadingTrue());
  }

  function hideLoading() {
    dispatch(changeLoadingFalse());
  }

  const onOpenContacts = () => {
    setOpenContacts((prevState) => !prevState.openContacts);
  };

  const chatClass = classNames({
    chat: true,
    'chat--open': openContacts,
  });

  const contactsClass = classNames({
    'chat__contact-list': true,
    'chat__contact-list--open': openContacts,
  });

  const changeLastOffSetHeight = (value) => {
    lastOffSetHeight.current = value;
  };

  const changeChatScrollTop = (value) => {
    chatScrollBar.current.scrollTop = value;
  };

  const onBackClickHandler = () => {
    setSelectedChatChannel({});
    setSelectedContact({});
    setCurrentMessages([]);
    loadChatContacts(0);
  };

  const createFilter = async (clickedContact, recordId, recordType) => {
    let loggedChatUserId;
    let selectedGroupId;
    switch (clickedContact.type) {
      case CHAT_CONTACT_TYPES.EMPLOYEE:
        loggedChatUserId = currentUser.chatUserId;
        break;
      case CHAT_CONTACT_TYPES.STUDENT_DIRECT:
        loggedChatUserId = selectedChatChannel.chatUserId;
        break;
      case CHAT_CONTACT_TYPES.RESPONSIBLE_DIRECT:
        loggedChatUserId = selectedChatChannel.chatUserId;
        break;
      case CHAT_CONTACT_TYPES.STUDENT_RESPONSIBLE:
        loggedChatUserId = selectedChatChannel.chatUserId;
        break;
      case CHAT_CONTACT_TYPES.GROUP:
        loggedChatUserId = currentUser.chatUserId;
        selectedGroupId = clickedContact.id;
        break;
      default:
        loggedChatUserId = 0;
    }

    return {
      employeeId: currentUser.id,
      recordId,
      recordType,
      loggedChatUserId,
      selectedChatUserId: clickedContact.chatUserId,
      selectedGroupId,
    };
  };

  const getHeight = () => {
    return chatScrollBar.current.scrollHeight + 400;
  };

  const scrollContainerToBottom = () => {
    setTimeout(() => {
      changeChatScrollTop(getHeight());
    }, 100);
  };

  const clearMessages = () => {
    setCurrentMessagesStorage([]);
    setCurrentMessages([]);
  };

  const messagesByFilter = (filter) => {
    return smarttalkaxios().post('/message/list', filter);
  };

  const loadMessagesBySelectedContact = async (clickedContact) => {
    setSelectedContact(clickedContact);
    showLoading();
    const filter = await createFilter(
      clickedContact,
      0,
      CHAT_RECORD_TYPE.NEW_RECORDS
    );
    messagesByFilter(filter)
      .then((res) => {
        setCurrentMessages(res.data);
        loadChatContacts(channelId, userChatId);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoading();
        scrollContainerToBottom();
      });
  };

  const getMessageIndex = (id) => {
    return currentMessages.findIndex((message) => message.chatId === id);
  };

  const removeMessageFromCurrentMessages = (id) => {
    const updatedMessages = [...currentMessages];
    const index = getMessageIndex(id);
    updatedMessages.splice(index, 1);
    setCurrentMessages(updatedMessages);
  };

  const showLoadingByDeletingMessage = (message) => {
    const updatedMessage = { ...message };
    const updatedMessages = [...currentMessages];
    const index = getMessageIndex(message.chatId);
    updatedMessage.isDeleting = true;
    updatedMessages[index] = updatedMessage;
    setCurrentMessages(updatedMessages);
    scrollContainerToBottom();
  };

  const deleteMessage = async (message) => {
    showLoadingByDeletingMessage(message);
    smarttalkaxios()
      .delete(`/message/${message.uid}`)
      .then(() => {
        removeMessageFromCurrentMessages(message.uid);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const markAsRead = async () => {
    const filter = await createFilter(
      selectedContact,
      0,
      CHAT_RECORD_TYPE.NEW_RECORDS
    );
    if (filter && filter.employeeId) {
      smarttalkaxios()
        .post('/message/markasread', filter)
        .then(() => {
          loadPendencyQuantity()
            .then((res) => {
              const { userPendency } = res;
              dispatch(updatePendencySummary(userPendency));
            })
            .catch(() => {});
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const loadSilentMessage = async (recordType) => {
    let lastRecord = 0;

    if (recordType === CHAT_RECORD_TYPE.NEW_RECORDS) {
      lastRecord =
        currentMessages.length > 0
          ? currentMessages[currentMessages.length - 1].chatId
          : 0;
    }
    const filter = await createFilter(selectedContact, lastRecord, recordType);
    messagesByFilter(filter)
      .then((res) => {
        const updatedMessages = [...currentMessages];
        res.data.forEach((newMessage) => {
          updatedMessages.push(newMessage);
        });
        setCurrentMessages(updatedMessages);
      })
      .catch((error) => {
        console.error(error);
        hideLoading();
      });
  };

  const onMessageReceived = (msg) => {
    const newMessage = JSON.parse(msg.body);
    const currentContact = getSelectedContactStorage();
    const newMessages = getCurrentMessagesStorage().map((m) => ({
      ...m,
      read: true,
    }));
    if (
      newMessage.senderId === currentContact.chatUserId &&
      newMessage.channelId === currentChannelIdRef.current
    ) {
      newMessage.sentAtUtcInMilli = newUTCDate();
      newMessages.push(newMessage);
      const set = new Set();
      const filteredMessages = newMessages.filter((newMsg) => {
        const duplicatedMessage = set.has(newMsg.uid);
        set.add(newMsg.uid);
        return !duplicatedMessage;
      });
      setCurrentMessages([...filteredMessages]);
    }
  };

  const onConnected = () => {
    stompClient.subscribe(
      `/user/${currentUser.chatUserId}/queue/messages`,
      onMessageReceived
    );
  };

  const onError = (err) => {
    console.error(err);
    // eslint-disable-next-line no-use-before-define
    connectToWs();
  };

  const connectToWs = () => {
    const sockJS = new SockJS(
      `${DEFAULT_SMART_TALK_API_URL}/ws?token=${token}`
    );
    stompClient = Stomp.over(sockJS);
    stompClient.connect({}, onConnected, onError);
    stompClient.debug = null;
  };

  const clearResetScrollHeight = () => {
    changeChatScrollTop(0);
    changeLastOffSetHeight(0);
  };

  const contactClickHandler = (clickedContact) => {
    clearResetScrollHeight();
    clearMessages();
    if (clickedContact.type === CHAT_CONTACT_TYPES.COMMUNICATION_CHANNEL) {
      setSelectedChatChannel(clickedContact);
      setSelectedContact({});
      loadChatContacts(clickedContact.id, clickedContact.chatUserId);
    } else {
      loadMessagesBySelectedContact(clickedContact);
    }
  };

  const sendMessage = (newMessage) => {
    stompClient.send('/app/chat', {}, JSON.stringify(newMessage));
    scrollContainerToBottom();
  };

  const updateCurrentMessagesByMessageUploadedFile = (
    message,
    currentMessageClone
  ) => {
    const updatedCurrentMessages = [...currentMessageClone];
    const foundMessageIndex = updatedCurrentMessages.findIndex(
      (current) => current.uid === message.uid
    );
    updatedCurrentMessages[foundMessageIndex] = message;
    setCurrentMessages(updatedCurrentMessages);
  };

  const updateMessageWithFileHandler = (message, data, currentMessageClone) => {
    const updatedMessage = { ...message };
    updatedMessage.file = data.fileId;
    updatedMessage.fileType = data.uploadType;
    updatedMessage.fileExtension = data.fileType;
    updatedMessage.duration = data.duration;
    sendMessage(updatedMessage);
    const updatedMessageClone = { ...updatedMessage };
    updatedMessageClone.file = data.link;
    updateCurrentMessagesByMessageUploadedFile(
      updatedMessageClone,
      currentMessageClone
    );
  };

  const notifyMessageError = (file, error, message) => {
    const newUploadFileError = {
      file,
      error,
      hasError: true,
      messageUid: message.uid,
    };
    setUploadFileError(newUploadFileError);
  };

  const uploadFileToServer = (file, message, currentMessageClone) => {
    upLoadFileProvider(file)
      .then(({ data }) => {
        updateMessageWithFileHandler(
          message,
          { ...data, uploadType: file.uploadType, duration: file.duration },
          currentMessageClone
        );
      })
      .catch((error) => {
        notifyMessageError(file, error, message);
      });
  };

  const onPushMessage = (message, file) => {
    const newMessage = {
      message,
      senderName: selectedChatChannel.chatUserId
        ? `${currentUser.name} - ${selectedChatChannel.name}`
        : currentUser.name,
      senderId: selectedChatChannel.chatUserId
        ? selectedChatChannel.chatUserId
        : currentUser.chatUserId,
      recipientId: selectedContact.chatUserId,
      recipientName: selectedContact.name,
      sentAtInMilli: new Date().getTime(),
      sentAtUtcInMilli: newUTCDate().getTime(),
      read: false,
      type: selectedContact.type,
      groupId:
        selectedContact.type === CHAT_CONTACT_TYPES.GROUP
          ? selectedContact.id
          : 0,
      employeeId: currentUser.id,
      uid: uuid(),
      file: file ? FILE_TO_UPLOAD : null,
      fileType: file ? file.type : null,
      channelId: selectedChatChannel ? selectedChatChannel.id : 0,
    };
    const updatedMessages = [...currentMessages];
    updatedMessages.push(newMessage);
    setCurrentMessages(updatedMessages);
    if (file) {
      uploadFileToServer(file, newMessage, updatedMessages);
    } else {
      sendMessage(newMessage);
    }
  };

  const onlyContactsWithUnreadMessagesFiltered = (event) => {
    let checked = false;
    if (event && event.target.checked) {
      checked = true;
      const onlyContactsWithUnreadMessages = contacts.filter(
        (contact) => contact.unreadMessage
      );
      setFilteredContactList(onlyContactsWithUnreadMessages);
    } else {
      setFilteredContactList(contacts);
    }
    setFilterChecked(checked);
  };

  const onFilterChange = (e) => {
    setFilterValue(e.target.value);
    const { value } = e.target;
    if (value) {
      const selectedList = contacts.filter(
        (cont) => cont.filterContent.indexOf(value.toUpperCase()) > -1
      );
      setFilteredContactList(selectedList);
    } else {
      setFilteredContactList(contacts);
    }
  };

  const setUploadFileErrorInCurrentMessage = () => {
    if (uploadFileError && uploadFileError.hasError) {
      const updatedCurrentMessages = [...currentMessages];
      const foundMessageIndex = updatedCurrentMessages.findIndex(
        (message) => message.uid === uploadFileError.messageUid
      );
      const updatedMessage = { ...currentMessages[foundMessageIndex] };
      updatedMessage.uploadFileError = uploadFileError;
      updatedCurrentMessages[foundMessageIndex] = updatedMessage;
      setCurrentMessages(updatedCurrentMessages);
    }
  };

  const printChat = (formData) => {
    showLoading();
    generateReport(formData)
      .then(async (res) => {
        openReport(res.data);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('report.error-generate')
        );
      });
  };
  const paramChat = (startDate, finalDate) => {
    const formData = new FormData();
    formData.append(REPORT_PARAMETERS.REPORTTYPE, REPORT_TYPES.RELA_MENSAG);
    formData.append(
      REPORT_PARAMETERS.pCO90UCRE,
      selectedChatChannel.chatUserId
        ? selectedChatChannel.chatUserId
        : currentUser.chatUserId
    );
    formData.append(REPORT_PARAMETERS.pCO90UCDE, selectedContact.chatUserId);
    formData.append(REPORT_PARAMETERS.pDATAINIC, getFormattedDateEN(startDate));
    formData.append(REPORT_PARAMETERS.pDATAFINA, getFormattedDateEN(finalDate));
    printChat(formData);
  };

  const addPlayerEventListener = () => {
    document.addEventListener(
      'play',
      (evt) => {
        const audios = document.getElementsByTagName('audio');
        for (let i = 0; i < audios.length; i += 1) {
          if (audios[i] !== evt.target) {
            audios[i].pause();
            audios[i].currentTime = 0;
          }
        }
      },
      true
    );
  };
  const getCurrentScrollPosition = () => {
    return chatScrollBar.current.scrollHeight - lastOffSetHeight.current;
  };

  const keepScrollPosition = () => {
    if (currentMessages.length > 50) {
      changeChatScrollTop(getCurrentScrollPosition());
    }
  };

  useEffect(() => {
    currentChannelIdRef.current = channelId;
  }, [channelId]);

  useEffect(() => {
    setFilteredContactList(contacts);
    setFilterValue('');
    if (filterChecked) {
      setFilterChecked(!filterChecked);
    }
  }, [contacts]);

  useEffect(() => {
    setCurrentMessagesStorage(currentMessages);
    if (selectedContact.chatUserId) {
      markAsRead();
      changeLastOffSetHeight(chatScrollBar.current.scrollHeight);
    }
  }, [currentMessages]);

  useEffect(() => {
    setSelectedContactStorage(selectedContact);
  }, [selectedContact]);

  useEffect(() => {
    if (currentUser.id) {
      connectToWs();
    }
  }, [currentUser]);

  const getUnreadMessageCount = (chatSummary, contact) => {
    const amount = chatSummary.reduce(
      (accum, summary) =>
        accum +
        (summary.senderId === contact.chatUserId ||
        summary.recipientId === contact.chatUserId
          ? summary.amount
          : 0),
      0
    );
    return amount;
  };

  const getUnreadMessageToChannel = (chatSummary, contact) => {
    const amount = chatSummary.reduce(
      (accum, summary) =>
        accum +
        (summary.senderId === contact.chatUserId &&
        summary.recipientId === selectedChatChannel.chatUserId
          ? summary.amount
          : 0),
      0
    );
    return amount;
  };

  const showLoadingMore = () => {
    setLoadingMore(true);
  };

  const hideLoadingMore = () => {
    setLoadingMore(false);
  };

  const showLoadMoreButton = () => {
    return currentMessages.length >= 50;
  };

  const loadMore = async () => {
    showLoadingMore();
    const lastRecord = currentMessages[0].chatId;
    const filter = await createFilter(
      selectedContact,
      lastRecord,
      CHAT_RECORD_TYPE.OLD_RECORDS
    );
    messagesByFilter(filter)
      .then((res) => {
        const messages = [...res.data];
        const updatedMessages = messages.concat(currentMessages);
        setCurrentMessages(updatedMessages);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideLoadingMore();
      });
  };

  useEffect(() => {
    const { chatSummary } = pendencySummary;
    const updatedFilteredContactList = filteredContactList.map((contact) => ({
      ...contact,
      unreadMessage: selectedChatChannel.chatUserId
        ? getUnreadMessageToChannel(chatSummary, contact)
        : getUnreadMessageCount(chatSummary, contact),
    }));
    if (selectedChatChannel && selectedChatChannel.chatUserId) {
      const unreadMessage = updatedFilteredContactList.reduce(
        (total, contact) => {
          return total + contact.unreadMessage;
        },
        0
      );
      setSelectedChatChannel({
        ...selectedChatChannel,
        unreadMessage,
      });
    }
    if (chatSummary) {
      let summary;

      if (selectedChatChannel && selectedChatChannel.chatUserId) {
        summary = chatSummary.find(
          (summ) =>
            selectedContact.chatUserId === summ.senderId &&
            summ.recipientId === selectedChatChannel.chatUserId
        );
      } else if (selectedContact.chatUserId) {
        summary = chatSummary.find(
          (summ) => selectedContact.chatUserId === summ.senderId
        );
      }
      if (summary) {
        loadSilentMessage(CHAT_RECORD_TYPE.NEW_RECORDS);
      }
    }
    setFilteredContactList(updatedFilteredContactList);
  }, [pendencySummary]);

  useEffect(() => {
    setUploadFileErrorInCurrentMessage();
  }, [uploadFileError]);

  useEffect(() => {
    addPlayerEventListener();
  }, []);

  useLayoutEffect(() => {
    keepScrollPosition();
  }, [currentMessages]);

  return (
    <div
      className={chatClass}
      role="presentation"
      onClick={openContacts ? onOpenContacts : null}
    >
      <div className={contactsClass}>
        <UserCard currentUser={currentUser} />
        {selectedChatChannel.chatUserId ? (
          <div className="chat__selected__channel">
            <ChatContact
              active
              contact={selectedChatChannel}
              onBackClickHandler={onBackClickHandler}
            />
          </div>
        ) : null}
        <ChatSearch
          onFilterChange={onFilterChange}
          filterValue={filterValue}
          checked={filterChecked}
          onCheckboxChange={onlyContactsWithUnreadMessagesFiltered}
        />
        <div
          className={
            contactType === CONTACT_CHAT_TYPE.STUDENT
              ? 'chat__contacts_with_scroll'
              : 'chat__contacts'
          }
        >
          <Scrollbar className="scroll chat__contacts-scroll" alwaysShowTracks>
            {filteredContactList.length
              ? filteredContactList.map((contact) => (
                  <ChatContact
                    key={contact.chatUserId}
                    contactClick={contactClickHandler}
                    active={contact.chatUserId === selectedContact.chatUserId}
                    contact={contact}
                  />
                ))
              : null}
          </Scrollbar>
        </div>
      </div>
      <div className="chat__dialog">
        {selectedContact.chatUserId ? (
          <ChatTopbar contact={selectedContact} parameters={paramChat} reset />
        ) : null}
        <div className="scroll chat__scroll" ref={chatScrollBar}>
          <div className="chat__dialog-messages-wrap">
            {currentMessages.length && selectedContact.chatUserId ? (
              <div className="chat__dialog-messages">
                {showLoadMoreButton() && (
                  <div className="d-flex justify-content-center mb-2">
                    <LoadMoreButton onClick={loadMore} loading={loadingMore} />
                  </div>
                )}
                {currentMessages.map((m, index) => (
                  <ChatBubble
                    key={`${m.uid}-${index * 2}`}
                    message={m}
                    active={
                      m.senderId === currentUser.chatUserId ||
                      m.employeeId === currentUser.id
                    }
                    deleteMessage={deleteMessage}
                    uploadFileToServer={uploadFileToServer}
                    currentMessages={currentMessages}
                  />
                ))}
              </div>
            ) : (
              <div className="chat__dialog-messages">
                <div className="chat__dialog-messages-empty">
                  <p>{t('general.no-message-to-show')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        {selectedContact.chatUserId ? (
          <ChatField sendMessage={onPushMessage} />
        ) : null}
      </div>
    </div>
  );
};

Chat.propTypes = {
  currentUser: CurrentUserProps.isRequired,
  contacts: ContactsProps.isRequired,
  loadChatContacts: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  pendencySummary: PropTypes.shape(PropTypes.shape).isRequired,
  contactType: PropTypes.string,
  channelId: PropTypes.number,
  userChatId: PropTypes.number,
};

Chat.defaultProps = {
  contactType: null,
  channelId: 0,
  userChatId: 0,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
    pendencySummary: state.pendencySummary,
  }))(memo(Chat))
);
